import { useState } from "react";
import not from "ramda/src/not";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { usePlacementWorksheetContext } from "../../contexts/PlacementWorksheetContext";
import { usePopupContext } from "../../contexts/PopupContext/context";
import { propIdString } from "../../helpers/propId";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import {
  IPlacementWorksheetUnitOccupant,
  putPlacementWorksheetOccupants,
} from "../../services/PlacementWorksheetApi";
import { LockUserInRoom } from "../../services/RoommateApi";
import { usePlacementWorksheetTableRowContext } from "../PlacementWorksheetTable/PlacementWorksheetTableRowContext";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";

export const useMatchHandler = (
  unitId: number,
  residents: IPlacementWorksheetUnitOccupant[]
) => {
  const { isAcc } = useSelectedNetworkImportTypes();
  const [isGenderConflictView, setIsGenderConflictView] = useState(
    isAcc ? false : false
  );
  const { setIsOpen } = useModalContext();
  const [accepted, setAccepted] = useState(false);
  const [isOverride, setIsOverride] = useState(!isAcc);
  const { selectedResidents, setSelectedResidents } = usePopupContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { showErrorSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const { l2Id, l3Id, updateUnitOccupants } = usePlacementWorksheetContext();
  const { getAuthToken } = useAuth();
  const [isLockIn, setIsLockIn] = useState(false);
  const { setIsFinished } = usePlacementWorksheetTableRowContext();

  const onHide = () => {
    setIsOpen(false);
  };

  const toggleOverride = () => setIsOverride(not);

  const handleError = (errorMsg?: string) =>
    showErrorSnackbar(errorMsg || "An Error Occurred");

  const stopLoading = () => setIsLoading(false);

  const handleSuccess = async (res: any) => {
    if (res.error) {
      if (res.error.code === "genders.mismatch" && !isAcc) {
        setIsGenderConflictView(true);
        return;
      } else {
        handleError(res.error.description);
        return;
      }
    }

    if (isLockIn && l2Id && l3Id && selectedResidents?.length) {
      await LockUserInRoom(
        selectedNetworkId,
        l2Id,
        l3Id,
        selectedResidents.concat(residents).map(propIdString),
        await getAuthToken()
      );
    }

    updateUnitOccupants(res.unitId, selectedResidents);
    setSelectedResidents([]);
    onHide();
    setIsFinished(true);
  };

  const handleMatch = async () => {
    if (l2Id && l3Id && selectedResidents?.length) {
      setIsLoading(true);
      putPlacementWorksheetOccupants(
        selectedNetworkId,
        l2Id,
        l3Id,
        unitId,
        isOverride,
        selectedResidents.map(propIdString),
        await getAuthToken()
      )
        .then(handleSuccess)
        .catch(handleError)
        .finally(stopLoading);
    }
  };

  const handleSwitchChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsLockIn(checked);
  };

  const handleGenderCOnfilctOverride = async () => {
    if (l2Id && l3Id && selectedResidents?.length) {
      setIsLoading(true);
      putPlacementWorksheetOccupants(
        selectedNetworkId,
        l2Id,
        l3Id,
        unitId,
        true,
        selectedResidents.map(propIdString),
        await getAuthToken()
      )
        .then(handleSuccess)
        .catch(handleError)
        .finally(stopLoading);
    }
  };

  return {
    selectedResidents,
    accepted,
    handleGenderCOnfilctOverride,
    onHide,
    isLockIn,
    isAcc,
    isOverride,
    toggleOverride,
    isGenderConflictView,
    isLoading,
    setAccepted,
    handleMatch,
    handleSwitchChange,
  };
};
